import React from "react";
import { Link, useParams } from "react-router-dom";
import { Anchor, ArrowLeft } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import PublicBreadcrumbs from "@/components/public/PublicBreadcrumbs";
import { enrichMarina, slugify } from "@/components/marinas/discovery/marinaDiscoveryUtils";
import { buildMarinaIntelligence } from "@/components/marinas/intelligence/marinaIntelligenceData";
import MarinaIntelligenceHero from "@/components/marinas/intelligence/MarinaIntelligenceHero";
import { ApproachPanel, CommunicationPanel, CoreDetailsPanel } from "@/components/marinas/intelligence/MarinaIntelligencePanels";
import MarinaMapIntelligence from "@/components/marinas/intelligence/MarinaMapIntelligence";
import MarinaMobileQuickActions from "@/components/marinas/intelligence/MarinaMobileQuickActions";
import MarinaStructuredData from "@/components/marinas/MarinaStructuredData";
import { marinaSlug } from "@/lib/marinaUrls";
import { localizeMarina, marinaSourceId } from "@/lib/marinaLocalization";
import { getLocalizedPath } from "@/lib/i18nRoutes";

export default function PublicMarinaDetailPage() {
  const { lang = "en", slug, marinaSlug: localizedMarinaSlug } = useParams();
  const detailSlug = slug || localizedMarinaSlug;
  const prefix = `/${lang}`;
  const localizedMarinasPath = getLocalizedPath("marinas", lang);
  const marinasBasePath = localizedMarinasPath !== "/404" ? localizedMarinasPath : `${prefix}/marinas`;
  const { data: marinas = [], isLoading } = useQuery({ queryKey: ["public-marina-detail"], queryFn: () => base44.entities.Marina.list("name", 1000), staleTime: 1000 * 60 * 10 });
  const marina = React.useMemo(() => marinas.map(enrichMarina).find((item) => item.slug === detailSlug || marinaSlug(item) === detailSlug || slugify(item.name) === detailSlug), [marinas, detailSlug]);
  const { data: translationRows = [] } = useQuery({ queryKey: ["public-marina-detail-localization", marina?.id, lang], queryFn: () => base44.entities.LocalizedContent.filter({ source_id: marinaSourceId(marina), source_type: "marina", language: lang, status: "published" }, "-localized_at", 1), enabled: !!marina?.id && lang !== "en", staleTime: 1000 * 60 * 10 });
  const displayMarina = React.useMemo(() => localizeMarina(marina, translationRows[0], lang), [marina, translationRows, lang]);
  const { data: reviews = [] } = useQuery({ queryKey: ["public-marina-rating-summary", marina?.id], queryFn: () => base44.entities.MarinaRating.filter({ marina_id: marina.id }, "-visited_at", 100), enabled: !!marina?.id });
  const averageRating = reviews.length ? reviews.reduce((sum, review) => sum + Number(review.rating || 0), 0) / reviews.length : Number(marina?.rating || 0);

  React.useLayoutEffect(() => {
    if (!displayMarina) return;
    document.title = displayMarina.localized_meta_title || `${displayMarina.name} Marina Guide | Marstick`;
    let description = document.querySelector('meta[name="description"]');
    if (!description) { description = document.createElement("meta"); description.setAttribute("name", "description"); document.head.appendChild(description); }
    description.setAttribute("content", displayMarina.localized_meta_description || `${displayMarina.name} in ${displayMarina.location}: services, berth capacity, VHF, map, facilities and sailing planning details.`);
    const hasPublishedTranslation = lang === "en" || Boolean(translationRows[0]);
    const robotsContent = hasPublishedTranslation ? "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" : "noindex, nofollow";
    ["robots", "googlebot"].forEach((name) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) { tag = document.createElement("meta"); tag.setAttribute("name", name); document.head.appendChild(tag); }
      tag.setAttribute("content", robotsContent);
    });
  }, [displayMarina, lang, translationRows]);

  if (isLoading) return <div className="maritime-container py-14"><div className="h-96 animate-pulse rounded-[2rem] bg-muted" /></div>;

  if (!marina) {
    return <div className="maritime-container py-14"><PublicBreadcrumbs items={[{ label: "Home", href: prefix }, { label: "Marinas", href: marinasBasePath }, { label: "Not found" }]} /><div className="rounded-[2rem] border border-border bg-card p-10 text-center"><Anchor className="mx-auto h-12 w-12 text-primary" /><h1 className="mt-4 text-3xl font-black">Marina not found</h1><Button asChild className="mt-5"><Link to={marinasBasePath}>Back to marinas</Link></Button></div></div>;
  }

  const lat = Number(marina.latitude);
  const lng = Number(marina.longitude);
  const intelligence = buildMarinaIntelligence(displayMarina);

  return (
    <div className="min-h-screen bg-slate-950 pb-24 text-white md:pb-0">
      <MarinaStructuredData marina={displayMarina} reviews={reviews} lang={lang} />
      <div className="maritime-container py-6 md:py-10">
        <PublicBreadcrumbs items={[{ label: "Home", href: prefix }, { label: "Marinas", href: marinasBasePath }, { label: displayMarina.country, href: `${marinasBasePath}/${slugify(displayMarina.country)}` }, { label: displayMarina.name }]} />
        <Button asChild variant="ghost" className="mb-4 text-cyan-50 hover:bg-white/10 hover:text-white"><Link to={marinasBasePath}><ArrowLeft className="h-4 w-4" /> Back to marina search</Link></Button>

        <div className="space-y-5 md:space-y-7">
          <MarinaIntelligenceHero marina={displayMarina} averageRating={averageRating} reviewCount={reviews.length} lat={lat} lng={lng} />

          <div className="rounded-[2rem] border border-cyan-300/20 bg-cyan-300/10 p-5 backdrop-blur-xl md:p-6">
            <h2 className="text-xl font-black text-white">Captain Summary</h2>
            <p className="mt-3 line-clamp-3 max-w-4xl text-sm leading-7 text-cyan-50/80 md:text-base">{displayMarina.description || `Professional briefing for ${displayMarina.name}, including approach, berth planning, communications, hazards and marina services.`}</p>
          </div>

          <div className="space-y-5">
            <CommunicationPanel marina={displayMarina} />
            <CoreDetailsPanel facts={intelligence.facts} />
            <ApproachPanel notes={intelligence.approach} />
            <MarinaMapIntelligence marina={displayMarina} lat={lat} lng={lng} />
            <div className="rounded-[2rem] border border-white/10 bg-white/[0.06] p-5 text-sm leading-7 text-cyan-50/72 backdrop-blur-xl">
              Planning context only. Always verify official charts, marina instructions, weather, and local notices before arrival.
            </div>
          </div>
        </div>
      </div>
      <MarinaMobileQuickActions marina={displayMarina} lat={lat} lng={lng} />
    </div>
  );
}